/**
 * CheltuieliGrid - Component generic reutilizabil pentru grile de cheltuieli
 * Folosit de: Medicina Muncii, Prime și Cadouri, Protecția Muncii, Alte Cheltuieli
 * Props:
 *   - token: JWT
 *   - apiPath: path-ul API (ex: "medicina-muncii" sau "cheltuieli/prime_cadouri")
 *   - title: titlul afișat (ex: "Prime și Cadouri")
 *   - testIdPrefix: prefix pentru data-testid
 */
import { useState, useEffect, memo, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS_SHORT = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];

const CheltuieliGrid = memo(function CheltuieliGrid({ token, apiPath, title, testIdPrefix }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [showExpenses, setShowExpenses] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/${apiPath}?an=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setData(d.employees || []);
        setExpenses(d.expenses || []);
      }
    } catch { toast.error('Eroare încărcare date'); }
    finally { setLoading(false); }
  }, [token, year, apiPath]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatNum = (n) => n ? n.toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '-';

  const handleCellClick = (emp, monthIdx) => {
    const monthStr = `${monthIdx + 1}`.padStart(2, '0');
    const luna = `${year}-${monthStr}`;
    const empExpenses = expenses.filter(e => {
      if (e.cnp !== emp.cnp) return false;
      if (e.tip === 'lunar') return e.luna_start === luna;
      if (e.tip === 'anual') {
        const [sy, sm] = e.luna_start.split('-').map(Number);
        for (let i = 0; i < 12; i++) {
          let dm = sm + i;
          let dy = sy + Math.floor((dm - 1) / 12);
          dm = ((dm - 1) % 12) + 1;
          if (`${dy}-${String(dm).padStart(2, '0')}` === luna) return true;
        }
      }
      return false;
    });
    setShowExpenses({ cnp: emp.cnp, name: emp.name, month: monthStr, luna, expenses: empExpenses });
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-tab`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(y => y - 1)} className="px-2 py-1 rounded hover:bg-slate-100 text-sm font-medium">&larr;</button>
          <span className="text-sm font-semibold text-slate-700">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="px-2 py-1 rounded hover:bg-slate-100 text-sm font-medium">&rarr;</button>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid={`add-${testIdPrefix}-btn`}>
          <Plus className="w-4 h-4 mr-1" />Adaugă cheltuială
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title} - {year}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : !data || data.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">Nu există date salariale pentru {year}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 w-8">Nr.</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 sticky left-8 bg-slate-50 z-10 min-w-[160px]">Angajat</th>
                    {MONTHS_SHORT.map((m, i) => (
                      <th key={i} className="px-2 py-2 text-right font-semibold text-slate-600 min-w-[70px]">{m}</th>
                    ))}
                    <th className="px-2 py-2 text-right font-bold text-slate-700 min-w-[80px]">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((emp, i) => (
                    <tr key={emp.cnp} className="border-b hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 text-slate-500 sticky left-0 bg-white z-10">{i + 1}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-800 sticky left-8 bg-white z-10">{emp.name}</td>
                      {MONTHS_SHORT.map((_, mi) => {
                        const val = emp.months[`${mi + 1}`.padStart(2, '0')] || 0;
                        return (
                          <td key={mi}
                            className={`px-2 py-1.5 text-right tabular-nums cursor-pointer hover:bg-blue-50 transition-colors ${
                              val > 0 ? 'text-slate-800 font-medium' : 'text-slate-300'
                            }`}
                            onClick={() => handleCellClick(emp, mi)}
                            title="Click pentru detalii"
                          >
                            {val > 0 ? formatNum(val) : '-'}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-right font-bold text-slate-800 tabular-nums">{formatNum(emp.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300">
                    <td className="px-2 py-2 sticky left-0 bg-slate-100 z-10"></td>
                    <td className="px-2 py-2 font-bold text-slate-800 sticky left-8 bg-slate-100 z-10">TOTAL</td>
                    {MONTHS_SHORT.map((_, mi) => {
                      const key = `${mi + 1}`.padStart(2, '0');
                      const total = (data || []).reduce((s, e) => s + (e.months[key] || 0), 0);
                      return (
                        <td key={mi} className="px-2 py-2 text-right font-bold text-slate-800 tabular-nums">
                          {total > 0 ? formatNum(total) : '-'}
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-right font-bold text-slate-900 tabular-nums">
                      {formatNum((data || []).reduce((s, e) => s + (e.total || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(showAdd || editExpense) && (
        <AddDialog
          token={token} apiPath={apiPath} title={title} testIdPrefix={testIdPrefix}
          employees={data || []} year={year} expense={editExpense}
          onClose={() => { setShowAdd(false); setEditExpense(null); }}
          onSaved={() => { setShowAdd(false); setEditExpense(null); fetchData(); }}
        />
      )}

      {showExpenses && (
        <DetailDialog
          info={showExpenses} token={token} apiPath={apiPath} testIdPrefix={testIdPrefix}
          onClose={() => setShowExpenses(null)}
          onEdit={(exp) => { setShowExpenses(null); setEditExpense(exp); }}
          onDeleted={() => { setShowExpenses(null); fetchData(); }}
        />
      )}
    </div>
  );
});

function AddDialog({ token, apiPath, title, testIdPrefix, employees, year, expense, onClose, onSaved }) {
  const [cnp, setCnp] = useState(expense?.cnp || '');
  const [suma, setSuma] = useState(expense?.suma ? String(expense.suma) : '');
  const [tip, setTip] = useState(expense?.tip || 'lunar');
  const [lunaStart, setLunaStart] = useState(expense?.luna_start || `${year}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [observatii, setObservatii] = useState(expense?.observatii || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!cnp || !suma) { toast.error('Selectați angajatul și introduceți suma'); return; }
    setSaving(true);
    try {
      const emp = employees.find(e => e.cnp === cnp);
      const url = expense
        ? `${API_URL}/api/salary-adapter/${apiPath}/${expense.id}`
        : `${API_URL}/api/salary-adapter/${apiPath}`;
      const res = await fetch(url, {
        method: expense ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ cnp, name: emp?.name || '', suma: parseFloat(suma), tip, luna_start: lunaStart, observatii: observatii.trim() })
      });
      if (res.ok) {
        toast.success(expense ? 'Cheltuială actualizată' : 'Cheltuială adăugată');
        onSaved();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Eroare');
      }
    } catch { toast.error('Eroare de rețea'); }
    finally { setSaving(false); }
  };

  const sumaNum = parseFloat(suma) || 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{expense ? 'Editare cheltuială' : `Adaugă cheltuială ${title}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!expense && (
            <div>
              <label className="text-xs font-medium text-slate-600">Angajat</label>
              <select className="w-full mt-1 border rounded px-3 py-2 text-sm" value={cnp}
                onChange={e => setCnp(e.target.value)} data-testid={`${testIdPrefix}-select-employee`}>
                <option value="">-- Selectați --</option>
                {employees.map(emp => <option key={emp.cnp} value={emp.cnp}>{emp.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">Suma (RON)</label>
            <Input className="mt-1" type="number" step="0.01" value={suma}
              onChange={e => setSuma(e.target.value)} placeholder="150" data-testid={`${testIdPrefix}-suma-input`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Tip cheltuială</label>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setTip('lunar')} data-testid={`${testIdPrefix}-tip-lunar`}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  tip === 'lunar' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                Luna curentă
                {tip === 'lunar' && sumaNum > 0 && <span className="block text-xs mt-1 font-normal">{sumaNum.toLocaleString('ro-RO')} RON pe o lună</span>}
              </button>
              <button onClick={() => setTip('anual')} data-testid={`${testIdPrefix}-tip-anual`}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  tip === 'anual' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                Pe un an
                {tip === 'anual' && sumaNum > 0 && <span className="block text-xs mt-1 font-normal">{(sumaNum / 12).toLocaleString('ro-RO', {maximumFractionDigits: 2})} RON/lună × 12</span>}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Luna de start</label>
            <input type="month" className="w-full mt-1 border rounded px-3 py-2 text-sm"
              value={lunaStart} onChange={e => setLunaStart(e.target.value)} data-testid={`${testIdPrefix}-luna-start`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Observații</label>
            <textarea className="w-full mt-1 border rounded px-3 py-2 text-sm resize-none" rows={2}
              value={observatii} onChange={e => setObservatii(e.target.value)}
              placeholder="Ce reprezintă această cheltuială..." data-testid={`${testIdPrefix}-observatii`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={`${testIdPrefix}-save-btn`}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {expense ? 'Salvează' : 'Adaugă'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ info, token, apiPath, testIdPrefix, onClose, onEdit, onDeleted }) {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/${apiPath}/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Cheltuială ștearsă'); onDeleted(); }
      else toast.error('Eroare la ștergere');
    } catch { toast.error('Eroare de rețea'); }
    finally { setDeleting(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">{info.name} — {MONTHS_LONG[parseInt(info.month) - 1]}</DialogTitle>
        </DialogHeader>
        {info.expenses.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Nicio cheltuială înregistrată pentru această lună.</p>
        ) : (
          <div className="space-y-2">
            {info.expenses.map(exp => (
              <div key={exp.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{exp.suma.toLocaleString('ro-RO')} RON</p>
                  <p className="text-xs text-slate-500">
                    {exp.tip === 'anual' ? `Anual (${(exp.suma / 12).toFixed(2)} RON/lună)` : 'Luna curentă'}
                    {' · '}Start: {exp.luna_start}
                  </p>
                  {exp.observatii && <p className="text-xs text-slate-600 mt-1 italic">{exp.observatii}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(exp)} data-testid={`edit-${testIdPrefix}-${exp.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(exp.id)} disabled={deleting === exp.id}
                    data-testid={`delete-${testIdPrefix}-${exp.id}`}>
                    {deleting === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 text-red-500" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CheltuieliGrid;
